import { useCallback, useEffect, useState } from "react";
import type {
  CashSummaryDto,
  CustomerDto,
  ExpenseCategoryDto,
  InvoiceDto,
  PaymentDto,
} from "@grizzly/shared";
import { api } from "./api.js";
import type { ApiClientError } from "./http.js";

export interface FinanceData {
  invoices: InvoiceDto[];
  payments: PaymentDto[];
  categories: ExpenseCategoryDto[];
  customers: CustomerDto[];
  cash: CashSummaryDto | null;
  error: string | null;
  reload(): void;
}

export function useFinanceData(): FinanceData {
  const [invoices, setInvoices] = useState<InvoiceDto[]>([]);
  const [payments, setPayments] = useState<PaymentDto[]>([]);
  const [categories, setCategories] = useState<ExpenseCategoryDto[]>([]);
  const [customers, setCustomers] = useState<CustomerDto[]>([]);
  const [cash, setCash] = useState<CashSummaryDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [i, p, c, cust, cashRes] = await Promise.all([
        api.listInvoices(),
        api.listPayments(),
        api.listExpenseCategories(),
        api.listCustomers(),
        api.listCash(),
      ]);
      setInvoices(i.items);
      setPayments(p.items);
      setCategories(c.items);
      setCustomers(cust.items);
      setCash(cashRes);
      setError(null);
    } catch (err) {
      setError((err as ApiClientError).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    invoices,
    payments,
    categories,
    customers,
    cash,
    error,
    reload: () => void load(),
  };
}
